import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Zap, Shield, Wind, Layers, Sun } from 'lucide-react';
import { PRODUCTS, ROUTES } from '@/lib/index';

const featureIcons: Record<string, React.ReactNode> = {
  'Microperforada': <Wind size={14} />,
  'Secado rápido': <Zap size={14} />,
  'Elástica 4 vías': <Layers size={14} />,
  'Ligera': <Zap size={14} />,
  'UV Protection': <Sun size={14} />,
  'Protección UV': <Sun size={14} />,
  'Full print': <Shield size={14} />,
  'Premium': <Shield size={14} />,
  'Transpirable': <Wind size={14} />,
};

const categoryColors: Record<string, string> = {
  'Deportivo': 'text-primary border-primary/30 bg-primary/10',
  'Pesca / Outdoor': 'text-accent border-accent/30 bg-accent/10',
  'Profesional': 'text-chart-5 border-chart-5/30 bg-chart-5/10',
};

const imgMap: Record<string, string> = {
  'playera-mc': '/mc-orig.jpg',
  'playera-sl': '/sl-orig.jpg',
  'playera-ml': '/ml-orig.jpg',
  'polo': '/polo-orig.jpg',
};

const imgStyle: Record<string, React.CSSProperties> = {};

export default function Catalog() {
  return (
    <div className="min-h-screen bg-background py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 40 }}
          className="text-center mb-14"
        >
          <p className="text-primary font-bold text-xs tracking-widest uppercase mb-3">NUESTROS PRODUCTOS</p>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-foreground mb-4">
            CATÁLOGO DE<br /><span className="text-primary">UNIFORMES</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Todos nuestros uniformes son <strong className="text-foreground">full print</strong> con diseño personalizado incluido.
            Tela microperforada premium.
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {PRODUCTS.map((product, i) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, type: 'spring', stiffness: 250, damping: 35 }}
              className="bg-card border border-border rounded-xl overflow-hidden group hover:border-primary/50 transition-colors duration-300"
            >
              {/* Image */}
              <div className="relative h-80 overflow-hidden bg-muted">
                <img
                  src={imgMap[product.id]}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
                {product.tag && (
                  <span className="absolute top-4 left-4 px-3 py-1 bg-primary text-primary-foreground text-[10px] font-black tracking-widest rounded">
                    {product.tag}
                  </span>
                )}
                <span className={`absolute top-4 right-4 px-3 py-1 text-[10px] font-bold tracking-wider rounded border ${categoryColors[product.category] || 'text-muted-foreground'}`}>
                  {product.category}
                </span>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-black tracking-widest uppercase text-foreground mb-2">{product.name}</h3>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{product.description}</p>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {product.features.map((f) => (
                    <span
                      key={f}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-muted text-muted-foreground text-[11px] font-bold tracking-wider rounded"
                    >
                      {featureIcons[f] || <Shield size={12} />}
                      {f}
                    </span>
                  ))}
                </div>

                {/* Styles */}
                <div className="mb-5">
                  <p className="text-[10px] text-muted-foreground tracking-widest uppercase font-bold mb-2">ESTILOS DISPONIBLES</p>
                  <div className="flex flex-wrap gap-2">
                    {product.styles.map((s) => (
                      <span key={s} className="px-3 py-1 border border-border text-xs font-bold text-muted-foreground rounded">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                <Link
                  to={ROUTES.ORDER}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase rounded hover:bg-primary/90 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  PEDIR ESTE MODELO <ChevronRight size={14} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border border-primary/30 rounded-xl p-8 text-center"
        >
          <h3 className="text-xl font-black uppercase tracking-widest text-foreground mb-3">
            ¿NO ENCUENTRAS LO QUE BUSCAS?
          </h3>
          <p className="text-muted-foreground mb-6">
            Trabajamos uniformes para cualquier tipo de proyecto y deporte. Contáctanos y diseñamos algo único para ti.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to={ROUTES.ORDER}
              className="px-8 py-3 bg-primary text-primary-foreground font-black text-xs tracking-widest uppercase rounded hover:bg-primary/90 transition-all"
            >
              SOLICITAR COTIZACIÓN
            </Link>
            <a
              href="https://wa.me/528333007124?text=Hola,%20quiero%20información%20sobre%20el%20catálogo"
              target="_blank"
              rel="noreferrer"
              className="px-8 py-3 border border-primary text-primary font-bold text-xs tracking-widest uppercase rounded hover:bg-primary/10 transition-all"
            >
              WHATSAPP DIRECTO
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
